﻿import { useState, useEffect, useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import {
  compressFilesToWebP, MAX_PICK_BYTES, MAX_PICK_LABEL,
  ALLOWED_IMAGE_MIME, ALLOWED_IMAGE_ACCEPT, ALLOWED_IMAGE_LABEL,
} from '@/lib/imageUtils';
import { formatFileSize } from '@/lib/fileFormat';
import { FilePicker, type PickedFile } from '@/components/ui/file-picker';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Eye, Image, ChevronLeft, ChevronRight, X, GripVertical, Settings,
  Layers, Clock, PlayCircle, PauseCircle, CheckCircle2, XCircle, Check, ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { z } from 'zod';
import { PERMISSIONS, DM_DESIGN_CUSTOM_CATEGORY } from '@pms/shared';
import { createDmDesignProjectSchema } from '@pms/shared';
import type {
  CreateDmDesignProjectInput, UpdateDmDesignProjectInput, UpdateDmDesignProjectStatusInput,
} from '@pms/shared';
import { dmTeamService } from '@/services/dm-team.service';
import { usersService } from '@/services/users.service';
import type {
  DmDesignProjectListItem, DmDesignCategory, DmDesignCategorySize, DmDesignProjectStatus,
  DmDesignProjectCategory, DmDesignProjectHoldHistory, DmUserRef,
} from '@/services/dm-team.service';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SearchInput } from '@/components/ui/search-input';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { DatePicker } from '@/components/ui/date-picker';
import { DesignCategoriesModal } from './DesignCategoriesModal';
import { AuthImage } from '@/components/ui/auth-image';
import { StatCard } from '@/components/ui/stat-card';

const PAGE_SIZE = 20;
const EMPTY_FILTERED_TEXT = 'No projects match these filters';

// ── Status helpers ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<DmDesignProjectStatus, string> = {
  pending:     'Pending',
  in_progress: 'In Progress',
  in_hold:     'On Hold',
  completed:   'Completed',
  cancelled:   'Cancelled',
};

const STATUS_COLORS: Record<DmDesignProjectStatus, string> = {
  pending:     'bg-gray-100 text-gray-700 border border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600',
  in_progress: 'bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-600',
  in_hold:     'bg-orange-100 text-orange-800 border border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-600',
  completed:   'bg-green-100 text-green-800 border border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-600',
  cancelled:   'bg-rose-100 text-rose-700 border border-rose-300 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-600',
};

const VALID_TRANSITIONS: Partial<Record<DmDesignProjectStatus, DmDesignProjectStatus[]>> = {
  pending:     ['in_progress', 'cancelled'],
  in_progress: ['in_hold', 'completed', 'cancelled'],
  in_hold:     ['in_progress', 'cancelled'],
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function safeName(val: string | null | undefined): string | null {
  if (!val) return null;
  return UUID_RE.test(val.trim()) ? null : val;
}

// The API builds user refs with json_build_object over a LEFT JOIN, so a missing
// user arrives as an object of nulls rather than null — check the id, not the object.
function userName(u: DmUserRef | null | undefined): string {
  if (!u?.id) return '—';
  return `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '—';
}



// ── View Project Dialog ────────────────────────────────────────────────────────

function ViewProjectDialog({ projectId, open, onClose }: { projectId: string; open: boolean; onClose: () => void }) {
  const [activeImg, setActiveImg] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);

  const { data } = useQuery({
    queryKey: ['dm-team', 'design-project', projectId],
    queryFn: () => dmTeamService.getDesignProject(projectId),
    enabled: open,
  });
  const project = data?.data;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-0" aria-describedby={undefined}>
        {!project ? (
          <>
            <DialogHeader className="px-4 sm:px-6 pt-6 pb-0">
              <DialogTitle>Design Details</DialogTitle>
            </DialogHeader>
            <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
          </>
        ) : (
          <>
            {/* Image gallery */}
            {project.images.length > 0 && (
              <div className="w-full overflow-hidden">
                {/* Main swiper */}
                <div className="relative overflow-hidden">
                  <Swiper
                    modules={[Navigation, Thumbs]}
                    navigation={project.images.length > 1}
                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                    onSlideChange={s => setActiveImg(s.activeIndex)}
                    className="dm-gallery-main"
                  >
                    {project.images.map(img => (
                      <SwiperSlide key={img.id}>
                        <AuthImage
                          src={`/dm-team/designer/images/${img.id}/file`}
                          alt={img.original_filename ?? ''}
                          className="w-full h-full"
                          style={{ objectFit: 'contain', objectPosition: 'center' }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {project.images.length > 1 && (
                    <span className="absolute bottom-2 right-2 z-10 bg-black/50 text-white text-xs font-medium px-2 py-0.5 rounded-full pointer-events-none">
                      {activeImg + 1} / {project.images.length}
                    </span>
                  )}
                </div>

                {/* Thumbnail swiper */}
                {project.images.length > 1 && (
                  <Swiper
                    modules={[FreeMode, Thumbs]}
                    freeMode
                    watchSlidesProgress
                    onSwiper={setThumbsSwiper}
                    slidesPerView="auto"
                    spaceBetween={6}
                    className="dm-gallery-thumbs"
                  >
                    {project.images.map(img => (
                      <SwiperSlide key={img.id}>
                        <AuthImage
                          src={`/dm-team/designer/images/${img.id}/file`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            )}

            {/* Details */}
            <div className="px-4 sm:px-6 pt-4 space-y-4">
              {/* Title + status */}
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">{project.title}</DialogTitle>
              </DialogHeader>

              {/* Description */}
              {project.description && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
                  {/* Descriptions are typed in a Textarea, so they carry real
                      newlines — without pre-wrap the browser collapses them and
                      several typed lines read as one run-on paragraph. */}
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">{project.description}</p>
                </div>
              )}

              {/* Status */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Status</p>
                <Badge className={`text-xs ${STATUS_COLORS[project.status]}`}>{STATUS_LABELS[project.status]}</Badge>
              </div>

              {/* Location */}
              {project.location && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Location</p>
                  <p className="text-sm font-medium">{project.location}</p>
                </div>
              )}

              {/* Category / Size */}
              {project.categories.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Category / Size</p>
                  <div className="flex flex-wrap gap-1.5">
                    {project.categories.map(c => {
                      const catLabel = safeName(c.category?.name ?? c.custom_category_text);
                      const sizeLabel = safeName(c.size?.name ?? c.custom_size_text);
                      if (!catLabel) return null;
                      return (
                        <span key={c.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm bg-muted/40">
                          <span className="font-medium">{catLabel}</span>
                          {sizeLabel && <><span className="text-muted-foreground">—</span><span className="text-muted-foreground">{sizeLabel}</span></>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Dates grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Publish Date</p>
                  <p className="text-sm font-medium">{project.publish_date ? format(new Date(project.publish_date), 'd MMM yyyy') : '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Start Date</p>
                  <p className="text-sm font-medium">{project.start_date ? format(new Date(project.start_date), 'd MMM yyyy') : '—'}</p>
                </div>
              </div>

              {/* People grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Designer</p>
                  <p className="text-sm font-medium">{userName(project.designer)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Assigned By</p>
                  <p className="text-sm font-medium">{userName(project.assigned_by)}</p>
                </div>
              </div>

              {/* Created grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Created On</p>
                  <p className="text-sm font-medium">{format(new Date(project.created_at), 'd MMM yyyy, h:mm a')}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Created By</p>
                  <p className="text-sm font-medium">{userName(project.created_by)}</p>
                </div>
              </div>

              {/* Hold reason (active) */}
              {project.status === 'in_hold' && project.hold_reason && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Hold Reason</p>
                  <p className="text-sm text-orange-600 font-medium whitespace-pre-wrap break-words">{project.hold_reason}</p>
                </div>
              )}

              {/* Hold history */}
              {project.hold_history.length > 0 && (
                <HoldHistorySection history={project.hold_history} />
              )}
            </div>

            {/* Close footer */}
            <div className="px-4 sm:px-6 py-4 flex justify-end">
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HoldHistorySection({ history }: { history: DmDesignProjectHoldHistory[] }) {
  return (
    <div className="rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50/60 dark:bg-orange-900/15 p-4">
      <p className="text-xs font-bold text-orange-800 dark:text-orange-400 uppercase tracking-wide mb-3">In Hold History</p>
      <div className="space-y-3">
        {history.map(h => (
          <div key={h.id} className="border-b border-orange-100 dark:border-orange-900/30 pb-3 last:border-0 last:pb-0">
            <div className="grid grid-cols-3 gap-2 text-xs mb-1.5">
              <div>
                <span className="text-muted-foreground block mb-0.5">Status</span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 font-medium">In Hold</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-0.5">Start Date</span>
                <span className="font-semibold">{format(new Date(h.start_date), 'd MMM yyyy')}</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-0.5">End Date</span>
                <span className="font-semibold">{h.end_date ? format(new Date(h.end_date), 'd MMM yyyy') : '—'}</span>
              </div>
            </div>
            {h.hold_reason && (
              <div>
                <span className="text-xs text-muted-foreground block mb-0.5">Hold Reason</span>
                <p className="text-xs text-orange-700 whitespace-pre-wrap break-words">{h.hold_reason}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Create Project Dialog ──────────────────────────────────────────────────────

type CategoryCombo = {
  category_id?: string;
  size_id?: string;
  custom_category_text?: string;
  custom_size_text?: string;
};

const projectFormSchema = createDmDesignProjectSchema.omit({ categories: true });
type ProjectFormData = z.infer<typeof projectFormSchema>;

type CategoryWithSizes = DmDesignCategory & { sizes: DmDesignCategorySize[] };

/**
 * An existing project category, mapped back into the shape the picker edits.
 *
 * Deliberately drops `c.id` — that is the dm_design_project_categories join-row
 * id, and the API replaces every row on save, so carrying it over would pass
 * uuid validation while pointing at nothing. Keys are omitted rather than set to
 * null because `custom_category_text` is optional but NOT nullable in the shared
 * schema, so an echoed null is a 400.
 */
function categoryToCombo(c: DmDesignProjectCategory): CategoryCombo {
  if (c.category) {
    return { category_id: c.category.id, ...(c.size ? { size_id: c.size.id } : {}) };
  }
  const sizeText = safeName(c.custom_size_text)?.trim();
  return {
    custom_category_text: safeName(c.custom_category_text)?.trim() || 'Other',
    ...(sizeText ? { custom_size_text: sizeText } : {}),
  };
}

/** DatePicker clears to '', which `z.string().date()` rejects. Empty means no date. */
function toDateValue(v: string | null | undefined): string | null {
  return v ? v.slice(0, 10) : null;
}

function buildProjectUpdate(d: ProjectFormData, combos: CategoryCombo[]): UpdateDmDesignProjectInput {
  return {
    title: d.title,
    description: d.description?.trim() || null,
    publish_date: toDateValue(d.publish_date),
    // Undefined when empty, so the service leaves the column alone.
    assigned_by_id: d.assigned_by_id ?? undefined,
    // start_date/end_date/designer_id are deliberately omitted: the service
    // skips undefined columns, leaving them to the status flow.
    categories: combos.map(c => ({
      ...(c.category_id ? { category_id: c.category_id } : {}),
      ...(c.size_id ? { size_id: c.size_id } : {}),
      ...(c.custom_category_text ? { custom_category_text: c.custom_category_text } : {}),
      ...(c.custom_size_text ? { custom_size_text: c.custom_size_text } : {}),
    })),
  };
}

/**
 * Assigned By picker, shared by the create and edit dialogs. Lists active and
 * inactive users; `fallback` keeps an assigner missing from the list (e.g.
 * deleted) visible instead of blank.
 */
function AssignedByField({ value, onChange, fallback }: {
  value: string;
  onChange: (id: string) => void;
  fallback?: DmUserRef | null;
}) {
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'picker', 'with-inactive'],
    queryFn: () => usersService.picker({ includeInactive: true }),
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => {
    const list = users.map(u => ({
      id: u.id,
      text: `${u.first_name} ${u.last_name}${u.employee_code ? ` (${u.employee_code})` : ''}`,
      inactive: !u.is_active,
    }));
    if (fallback && !users.some(u => u.id === fallback.id)) {
      list.unshift({ id: fallback.id, text: `${fallback.first_name} ${fallback.last_name}`, inactive: false });
    }
    return list;
  }, [users, fallback]);

  const q = search.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.text.toLowerCase().includes(q)) : options;
  const selected = options.find(o => o.id === value);

  const close = () => { setOpen(false); setSearch(''); };

  // Close on outside click / Escape; Escape is stopped so it doesn't also close the dialog.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Inline panel, not a Radix Select/Popover: inside a Radix Dialog a portalled
  // search input can't keep focus, so typing would do nothing.
  return (
    <div ref={rootRef} className="relative">
      <Label>Assigned By</Label>
      <Button
        type="button"
        variant="outline"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className="mt-1 w-full justify-between font-normal"
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.text : 'Select user'}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg">
          <div className="border-b border-border/60 p-2">
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search by name or code…"
              containerClassName="w-full"
            />
          </div>
          <div className="max-h-60 overflow-y-auto overscroll-contain p-1">
            {filtered.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); close(); }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                  o.id === value ? 'bg-primary/10' : 'hover:bg-muted/50',
                )}
              >
                <Check className={cn('h-4 w-4 shrink-0', o.id === value ? 'opacity-100' : 'opacity-0')} />
                <span className="truncate">{o.text}</span>
                {o.inactive && <span className="ml-auto shrink-0 text-xs text-muted-foreground">Inactive</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground">No users found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** The category picker, shared by the create and edit dialogs. */
function CategoryComboField({ categories, combos, setCombos }: {
  categories: CategoryWithSizes[];
  combos: CategoryCombo[];
  setCombos: React.Dispatch<React.SetStateAction<CategoryCombo[]>>;
}) {
  const [selectedCatId, setSelectedCatId] = useState('');
  const [selectedSizeId, setSelectedSizeId] = useState('');
  const [customSizeText, setCustomSizeText] = useState('');

  const getCatSizes = (catId: string) => categories.find(c => c.id === catId)?.sizes ?? [];

  const addCombo = () => {
    if (selectedCatId === '__other__') {
      setCombos(c => [...c, { custom_category_text: 'Other', custom_size_text: customSizeText.trim() || undefined }]);
      setSelectedCatId('');
      setCustomSizeText('');
    } else {
      if (!selectedCatId) return;
      setCombos(c => [...c, { category_id: selectedCatId, size_id: selectedSizeId || undefined }]);
      setSelectedCatId('');
      setSelectedSizeId('');
    }
  };

  return (
    <div>
      <Label>Categories</Label>
      <div className="space-y-2 mt-1">
        {combos.map((c, i) => {
          const cat = categories.find(x => x.id === c.category_id);
          const sz = cat?.sizes.find((s: DmDesignCategorySize) => s.id === c.size_id);
          const label = cat?.name ?? c.custom_category_text ?? '';
          const sizeLabel = sz?.name ?? c.custom_size_text ?? '';
          return (
            <div key={i} className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs flex-1">{label}{sizeLabel ? ` · ${sizeLabel}` : ''}</Badge>
              <button type="button" onClick={() => setCombos(c => c.filter((_, j) => j !== i))} className="text-destructive hover:text-destructive/80">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
        <div className="flex gap-2">
          <Select value={selectedCatId} onValueChange={v => { setSelectedCatId(v); setSelectedSizeId(''); setCustomSizeText(''); }}>
            <SelectTrigger className="flex-1"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              <SelectItem value="__other__">Other (manual)</SelectItem>
            </SelectContent>
          </Select>
          {selectedCatId === '__other__' ? (
            <Input placeholder="Size (optional)" value={customSizeText} onChange={e => setCustomSizeText(e.target.value)} className="flex-1" />
          ) : (
            selectedCatId && getCatSizes(selectedCatId).length > 0 && (
              <Select value={selectedSizeId} onValueChange={setSelectedSizeId}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Size" /></SelectTrigger>
                <SelectContent>{getCatSizes(selectedCatId).map((s: DmDesignCategorySize) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )
          )}
          <Button type="button" variant="outline" size="icon" onClick={addCombo}
            disabled={
              !selectedCatId ||
              (selectedCatId !== '__other__' && getCatSizes(selectedCatId).length > 0 && !selectedSizeId)
            }>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateProjectDialog({ open, onClose, categories, canSetAssignedBy }: {
  open: boolean; onClose: () => void; categories: CategoryWithSizes[]; canSetAssignedBy: boolean;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [combos, setCombos] = useState<CategoryCombo[]>([]);

  // No designer here: a new project only has the person who assigned it, which
  // defaults to whoever is logged in. The designer is set when someone moves it
  // to In Progress.
  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: { assigned_by_id: user?.id },
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateDmDesignProjectInput) => dmTeamService.createDesignProject(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-projects'] });
      onClose();
      form.reset({ assigned_by_id: user?.id });
      setCombos([]);
      toast.success('Project created');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Design Project</DialogTitle></DialogHeader>
        <form onSubmit={form.handleSubmit(d => {
            createMutation.mutate({ ...d, categories: combos } as CreateDmDesignProjectInput);
          })} className="space-y-4">
          <div><Label>Title *</Label><Input {...form.register('title')} className="mt-1" /></div>
          <div><Label>Description</Label><Textarea {...form.register('description')} className="mt-1 resize-none" rows={3} /></div>
          <div>
            <Label>Publish Date</Label>
            <DatePicker
              value={form.watch('publish_date') || ''}
              onChange={v => form.setValue('publish_date', v || null, { shouldValidate: true })}
            />
          </div>
          {canSetAssignedBy && (
            <AssignedByField
              value={form.watch('assigned_by_id') || ''}
              onChange={v => form.setValue('assigned_by_id', v, { shouldValidate: true })}
            />
          )}
          <CategoryComboField categories={categories} combos={combos} setCombos={setCombos} />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending}>Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Project Dialog ───────────────────────────────────────────────────────

/**
 * Seeded from the list row rather than a detail fetch: the row already carries
 * every field this dialog edits, so a fetch would add a loading state for data
 * that is never rendered. Mount it conditionally — defaultValues and the combo
 * initializer only run once, so a persistent instance would keep showing the
 * first row that was opened.
 */
function EditProjectDialog({ project, open, onClose, categories, canSetAssignedBy }: {
  project: DmDesignProjectListItem; open: boolean; onClose: () => void; categories: CategoryWithSizes[];
  canSetAssignedBy: boolean;
}) {
  const qc = useQueryClient();
  const [combos, setCombos] = useState<CategoryCombo[]>(() => project.categories.map(categoryToCombo));

  // The create schema, not the update one: the four visible fields carry
  // identical rules, and it keeps `title` required — update makes it optional,
  // which would let a blank title through as a silent no-op save.
  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      title: project.title,
      description: project.description ?? '',
      publish_date: toDateValue(project.publish_date),
      assigned_by_id: project.assigned_by?.id ?? null,
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateDmDesignProjectInput) => dmTeamService.updateDesignProject(project.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-projects'] });
      // Singular key — the plural prefix above does not cover it, and the view
      // dialog would otherwise still show the old values.
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-project', project.id] });
      onClose();
      toast.success('Project updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update'),
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit Design Project</DialogTitle></DialogHeader>
        <form onSubmit={form.handleSubmit(d => {
            updateMutation.mutate(buildProjectUpdate(d, combos));
          })} className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input {...form.register('title')} className="mt-1" />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.title.message}</p>
            )}
          </div>
          <div>
            <Label>Description</Label>
            <Textarea {...form.register('description')} className="mt-1 resize-none" rows={3} />
            {form.formState.errors.description && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.description.message}</p>
            )}
          </div>
          <div>
            <Label>Publish Date</Label>
            <DatePicker
              value={form.watch('publish_date') || ''}
              onChange={v => form.setValue('publish_date', v || null, { shouldValidate: true })}
            />
            {form.formState.errors.publish_date && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.publish_date.message}</p>
            )}
          </div>
          {canSetAssignedBy && (
            <AssignedByField
              value={form.watch('assigned_by_id') || ''}
              onChange={v => form.setValue('assigned_by_id', v, { shouldValidate: true })}
              fallback={project.assigned_by}
            />
          )}
          <CategoryComboField categories={categories} combos={combos} setCombos={setCombos} />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={updateMutation.isPending}>Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline Status Badge ────────────────────────────────────────────────────────

/**
 * Rejection reason for a picked design image, or null when it is acceptable.
 *
 * Both rules mirror the backend, which rejects anything outside its mime allow-list
 * and truncates past its own size limit — catching them here means the user is told
 * which file is wrong instead of losing the whole upload to a 400.
 */
function validateDesignImage(file: File): string | null {
  if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(file.type)) {
    return `Not a supported image — ${ALLOWED_IMAGE_LABEL} only`;
  }
  if (file.size > MAX_PICK_BYTES) {
    return `Too large — ${formatFileSize(file.size)} (max ${MAX_PICK_LABEL})`;
  }
  return null;
}

function StatusBadge({ project, canChangeStatus }: { project: DmDesignProjectListItem; canChangeStatus: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [holdReasonOpen, setHoldReasonOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeLocation, setCompleteLocation] = useState('');
  // PickedFile[] rather than a FileList: a FileList is read-only, so a second pick
  // would replace the first selection instead of adding to it. The wrapper also
  // carries each file's rejection reason so an invalid one can be shown and removed
  // on its own, instead of the whole selection being discarded.
  const [completeFiles, setCompleteFiles] = useState<PickedFile[]>([]);
  // Compression + upload happen before the status call, so the dialog needs its
  // own pending flag — mutation.isPending only covers the status request.
  const [completing, setCompleting] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: UpdateDmDesignProjectStatusInput) => dmTeamService.updateDesignProjectStatus(project.id, body),
    onSuccess: (_, variables) => {
      qc.setQueriesData<{ data: DmDesignProjectListItem[]; pagination: any }>(
        { queryKey: ['dm-team', 'design-projects'], exact: false },
        (old) => {
          // The prefix also matches the status-counts query, whose data is an
          // object, not a row array — patching it would throw on .map/.filter.
          // Leave it to the invalidation below.
          if (!Array.isArray(old?.data)) return old;
          if (variables.status === 'completed' || variables.status === 'cancelled') {
            return { ...old, data: old.data.filter(p => p.id !== project.id) };
          }
          return {
            ...old,
            data: old.data.map(p => {
              if (p.id !== project.id) return p;
              const patch: Partial<DmDesignProjectListItem> = { status: variables.status };
              if (variables.status === 'in_progress' && user) {
                patch.designer = { id: user.id, first_name: user.first_name ?? '', last_name: user.last_name ?? '' };
              }
              return { ...p, ...patch };
            }),
          };
        }
      );
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-projects'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-project', project.id] });
      setOpen(false);
      setHoldReasonOpen(false);
      setHoldReason('');
      toast.success('Status updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed'),
  });

  const handleStatusClick = (s: DmDesignProjectStatus) => {
    if (s === 'completed') {
      setOpen(false);
      setCompleteOpen(true);
    } else if (s === 'in_hold') {
      setOpen(false);
      setHoldReasonOpen(true);
    } else {
      mutation.mutate({ status: s });
    }
  };

  const submitHold = () => {
    mutation.mutate({ status: 'in_hold', hold_reason: holdReason.trim() || null });
  };

  const invalidCount = completeFiles.filter(f => f.error).length;
  const validFiles = completeFiles.filter(f => !f.error).map(f => f.file);

  const resetComplete = () => {
    setCompleteOpen(false);
    setCompleteLocation('');
    setCompleteFiles([]);
  };

  const submitComplete = async () => {
    // All three are mandatory: a design is only "completed" once the finished work is
    // attached. The button is already disabled without them — this guards the
    // Enter key and any state that slips past it.
    if (!completeLocation.trim()) { toast.error('Location is required'); return; }
    if (invalidCount > 0) { toast.error('Remove the invalid files before completing'); return; }
    if (validFiles.length === 0) { toast.error('At least one image is required to complete a design'); return; }

    setCompleting(true);
    let statusCallStarted = false;
    try {
      // Upload first, then flip the status. The other order can leave a project
      // completed with no images when the upload fails — and completed projects
      // can no longer change status, so it could never be corrected.
      const compressed = await compressFilesToWebP(validFiles);
      await dmTeamService.uploadProjectImages(project.id, compressed);
      statusCallStarted = true;
      await mutation.mutateAsync({ status: 'completed', location: completeLocation.trim() });
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-projects'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-project', project.id] });
      resetComplete();
    } catch (e: any) {
      // Status failures already toast in mutation.onError; this covers the
      // compression and upload steps, which have no mutation behind them.
      if (!statusCallStarted) {
        toast.error(e?.response?.data?.error?.message ?? 'Failed to upload images');
      }
    } finally {
      setCompleting(false);
    }
  };

  if (!canChangeStatus) {
    return <Badge className={`text-xs ${STATUS_COLORS[project.status]}`}>{STATUS_LABELS[project.status]}</Badge>;
  }

  return (
    <span onClick={e => e.stopPropagation()}>
      <Dialog open={open} onOpenChange={setOpen}>
        <button onClick={e => { e.stopPropagation(); setOpen(true); }}>
          <Badge className={`text-xs cursor-pointer hover:opacity-80 ${STATUS_COLORS[project.status]}`}>{STATUS_LABELS[project.status]}</Badge>
        </button>
        <DialogContent className="max-w-xs" aria-describedby={undefined} onCloseAutoFocus={e => e.preventDefault()}>
          <DialogHeader><DialogTitle>Change Status</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(VALID_TRANSITIONS[project.status] ?? []).map(s => (
              <button key={s} onClick={() => handleStatusClick(s)}
                disabled={mutation.isPending}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-80 ${STATUS_COLORS[s]}`}>
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Hold reason dialog */}
      <Dialog open={holdReasonOpen} onOpenChange={v => { if (!v) { setHoldReasonOpen(false); setHoldReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Put On Hold</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter the reason for putting this project on hold.</p>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide">Hold Reason</Label>
              <Textarea
                className="mt-1 resize-none"
                rows={3}
                placeholder="Enter reason…"
                value={holdReason}
                onChange={e => setHoldReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHoldReasonOpen(false); setHoldReason(''); }}>Cancel</Button>
            <Button onClick={submitHold} disabled={mutation.isPending} className="bg-orange-500 hover:bg-orange-600 text-white">
              Confirm Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Design dialog */}
      <Dialog open={completeOpen} onOpenChange={v => { if (!v && !completing) resetComplete(); }}>
        {/* grid-cols override: DialogContent is a grid whose auto column grows to fit
            its widest child, so one long filename would stretch the whole dialog. */}
        <DialogContent className="grid-cols-[minmax(0,1fr)]" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Complete Design</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide">Location *</Label>
              <Input className="mt-1" placeholder="Enter location…" value={completeLocation}
                disabled={completing}
                onChange={e => setCompleteLocation(e.target.value)} />
            </div>
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wide">Upload Images *</Label>
                {completeFiles.length > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    {validFiles.length} ready
                    {invalidCount > 0 && (
                      <span className="text-rose-600 dark:text-rose-400"> · {invalidCount} invalid</span>
                    )}
                  </span>
                )}
              </div>

              <FilePicker
                multiple
                files={completeFiles}
                onFilesChange={setCompleteFiles}
                validate={validateDesignImage}
                accept={ALLOWED_IMAGE_ACCEPT}
                disabled={completing}
                placeholder={completeFiles.length > 0 ? 'Add more images' : 'Choose images'}
                hint={`${ALLOWED_IMAGE_LABEL} · max ${MAX_PICK_LABEL} each`}
              />

              {invalidCount > 0 ? (
                <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
                  Remove {invalidCount} invalid file{invalidCount > 1 ? 's' : ''} to continue.
                </p>
              ) : completeFiles.length === 0 ? (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  At least one image is required to complete a design.
                </p>
              ) : (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Sizes shown are the original files — each image is compressed before upload.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={completing} onClick={resetComplete}>Cancel</Button>
            <Button
              disabled={
                !completeLocation.trim() || validFiles.length === 0 || invalidCount > 0 ||
                completing || mutation.isPending
              }
              onClick={submitComplete}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {completing ? 'Completing…' : 'Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
}

// ── Static Row (no drag handle — for users without reorder access) ────────────

function StaticRow({ row, columns, onRowClick }: { row: DmDesignProjectListItem; columns: Column<DmDesignProjectListItem>[]; onRowClick?: () => void }) {
  const rowBg = row.status === 'in_progress'
    ? 'bg-amber-50 hover:bg-amber-100/70 dark:bg-amber-900/15 dark:hover:bg-amber-900/25'
    : row.status === 'in_hold'
      ? 'bg-orange-50 hover:bg-orange-100/70 dark:bg-orange-900/15 dark:hover:bg-orange-900/25'
      : 'hover:bg-muted/30';
  return (
    <tr onClick={onRowClick} className={`border-b transition-colors cursor-pointer ${rowBg}`}>
      <td className="pl-3 pr-1 py-2.5 w-8" />
      {columns.map(col => (
        <td key={col.key} className={`px-3 py-2.5 text-sm ${col.className ?? ''}`}>
          {col.cell ? col.cell(row) : String((row as any)[col.key] ?? '')}
        </td>
      ))}
    </tr>
  );
}

// ── Sortable Row (Active tab) ─────────────────────────────────────────────────

function SortableRow({ row, columns, onRowClick }: { row: DmDesignProjectListItem; columns: Column<DmDesignProjectListItem>[]; onRowClick?: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const rowBg = row.status === 'in_progress'
    ? 'bg-amber-50 hover:bg-amber-100/70 dark:bg-amber-900/15 dark:hover:bg-amber-900/25'
    : row.status === 'in_hold'
      ? 'bg-orange-50 hover:bg-orange-100/70 dark:bg-orange-900/15 dark:hover:bg-orange-900/25'
      : 'hover:bg-muted/30';
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      onClick={() => { if (!isDragging) onRowClick?.(); }}
      className={`border-b transition-colors cursor-pointer ${rowBg}`}
    >
      <td className="pl-3 pr-1 py-2.5 text-muted-foreground">
        <span {...attributes} {...listeners} className="inline-flex cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4" />
        </span>
      </td>
      {columns.map(col => (
        <td key={col.key} className={`px-3 py-2.5 text-sm ${col.className ?? ''}`}>
          {col.cell ? col.cell(row) : String((row as any)[col.key] ?? '')}
        </td>
      ))}
    </tr>
  );
}

// ── Mobile card (shown only below md breakpoint) ──────────────────────────────

function MobileProjectCard({ project, number, onClick, onEdit }: {
  project: DmDesignProjectListItem; number?: number; onClick: () => void; onEdit?: () => void;
}) {
  const bgClass =
    project.status === 'in_progress' ? 'bg-amber-50/60 dark:bg-amber-900/15' :
    project.status === 'in_hold'     ? 'bg-orange-50/60 dark:bg-orange-900/15' : '';
  const visibleCats = project.categories.slice(0, 2);
  const extra = project.categories.length - 2;
  return (
    <div
      onClick={onClick}
      className={`flex items-start justify-between rounded-lg border p-3 cursor-pointer hover:bg-muted/30 transition-colors ${bgClass}`}
    >
      {number != null && (
        <span className="mr-2.5 mt-0.5 shrink-0 text-sm font-medium text-muted-foreground tabular-nums">{number}</span>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium truncate">{project.title}</p>
        {project.description && (
          <p className="text-sm text-muted-foreground truncate">{project.description}</p>
        )}
        {visibleCats.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {visibleCats.map((c) => {
              const catName = safeName(c.category?.name ?? c.custom_category_text);
              const sizeName = safeName(c.size?.name ?? c.custom_size_text);
              if (!catName) return null;
              return (
                <span key={c.id} className="text-xs bg-muted px-1.5 py-0.5 rounded">
                  {catName}{sizeName ? ` · ${sizeName}` : ''}
                </span>
              );
            })}
            {extra > 0 && <span className="text-xs bg-muted px-1.5 py-0.5 rounded">+{extra}</span>}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <Badge className={`text-xs ${STATUS_COLORS[project.status]}`}>
            {STATUS_LABELS[project.status]}
          </Badge>
          {project.publish_date && (
            <span className="text-xs text-muted-foreground">
              {format(new Date(project.publish_date), 'd MMM yyyy')}
            </span>
          )}
        </div>
        {project.designer && (
          <p className="text-xs text-muted-foreground">
            {project.designer.first_name} {project.designer.last_name}
          </p>
        )}
      </div>
      <div className="ml-3 flex shrink-0 items-center gap-1">
        {onEdit && (
          <button
            type="button"
            aria-label="Edit project"
            onClick={e => { e.stopPropagation(); onEdit(); }}
            className="flex h-11 w-11 items-center justify-center rounded-md text-teal-600 hover:bg-muted"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        <Eye className="mt-0.5 h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, total, onPageChange }: {
  page: number; totalPages: number; total: number; onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t px-3 py-2">
      <span className="text-xs text-muted-foreground">
        {total} total · Page {page} of {totalPages}
      </span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" className="h-9 w-9 p-0"
          disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" className="h-9 w-9 p-0"
          disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function DesignerPage() {
  const qc = useQueryClient();
  const permission = usePermission();
  const canCreate = permission.has(PERMISSIONS.DM_TEAM_DESIGNER_CREATE);
  const canEdit = permission.has(PERMISSIONS.DM_TEAM_DESIGNER_EDIT);
  const canDelete = permission.has(PERMISSIONS.DM_TEAM_DESIGNER_DELETE);

  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'cancelled'>('active');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewProjectId, setViewProjectId] = useState<string | null>(null);
  // Holds the row itself, not an id — the edit dialog seeds straight from it.
  const [editProject, setEditProject] = useState<DmDesignProjectListItem | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploadProjectId, setUploadProjectId] = useState<string | null>(null);
  // PickedFile[] not FileList — see the Complete Design dialog's state for why.
  const [uploadFiles, setUploadFiles] = useState<PickedFile[]>([]);
  const [activeRows, setActiveRows] = useState<DmDesignProjectListItem[]>([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [designerFilter, setDesignerFilter] = useState('');
  const [assignedByFilter, setAssignedByFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const debouncedSearch = useDebouncedValue(search);

  // Two flags, deliberately. `hasActiveFilters` follows the raw input so Clear
  // appears on the first keystroke; `isFiltered` follows the debounced value so
  // reorder and row numbering flip in step with the rows actually on screen
  // rather than 350ms ahead of them.
  const hasActiveFilters = !!(search || categoryFilter || designerFilter || assignedByFilter || dateFrom || dateTo);
  const isFiltered = !!(debouncedSearch || categoryFilter || designerFilter || assignedByFilter || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch(''); setCategoryFilter(''); setDesignerFilter(''); setAssignedByFilter('');
    setDateFrom(''); setDateTo(''); setPage(1);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { data: metaData } = useQuery({
    queryKey: ['dm-team', 'designer-meta'],
    queryFn: () => dmTeamService.getDesignerMeta(),
  });

  // `|| undefined` matters beyond style: axios omits undefined params, so an
  // empty filter never reaches the backend's `if (query.x)` guards at all.
  const filters = {
    q:           debouncedSearch || undefined,
    category_id: categoryFilter  || undefined,
    designer_id: designerFilter  || undefined,
    assigned_by_id: assignedByFilter || undefined,
    date_from:   dateFrom        || undefined,
    date_to:     dateTo          || undefined,
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'design-projects', { page, tab: activeTab, ...filters }],
    queryFn: () => dmTeamService.listDesignProjects({ page, limit: PAGE_SIZE, tab: activeTab, ...filters }),
  });

  // Keyed under the design-projects prefix on purpose: every mutation already
  // invalidates that prefix, so the cards refresh on create, delete and status
  // change without any mutation having to know the cards exist.
  const { data: countsData } = useQuery({
    queryKey: ['dm-team', 'design-projects', 'counts', filters],
    queryFn: () => dmTeamService.getDesignProjectCounts(filters),
  });
  const counts = countsData?.data;

  useEffect(() => {
    if (activeTab === 'active' && data?.data) setActiveRows(data.data);
  }, [data, activeTab]);

  const reorderMutation = useMutation({
    mutationFn: (items: { id: string; order_index: number }[]) =>
      dmTeamService.reorderDesignProjects({ items }),
    onError: () => {
      toast.error('Failed to save order');
      if (data?.data) setActiveRows(data.data);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deleteDesignProject(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-projects'] }); setDeleteId(null); toast.success('Project deleted'); },
    onError: () => toast.error('Failed to delete'),
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ id, files }: { id: string; files: File[] }) => {
      const compressed = await compressFilesToWebP(files);
      return dmTeamService.uploadProjectImages(id, compressed);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'design-projects'] }); setUploadProjectId(null); setUploadFiles([]); toast.success('Images uploaded'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Upload failed'),
  });

  const invalidUploadCount = uploadFiles.filter(f => f.error).length;
  const validUploadFiles = uploadFiles.filter(f => !f.error).map(f => f.file);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setActiveRows(rows => {
      const oldIdx = rows.findIndex(r => r.id === active.id);
      const newIdx = rows.findIndex(r => r.id === over.id);
      const reordered = arrayMove(rows, oldIdx, newIdx);
      // Reuse the order_index slots this page already occupies instead of
      // renumbering from zero - values are sparse (0..179 for 34 rows) and
      // renumbering would collide with the rows on other pages.
      const slots = rows.map(r => r.order_index).sort((a, b) => a - b);
      reorderMutation.mutate(reordered.map((r, i) => ({ id: r.id, order_index: slots[i]! })));
      return reordered;
    });
  };

  const categories = metaData?.data?.categories ?? [];
  const pagination = data?.pagination;
  // Reorder and delete are additionally gated on their whitelists (Settings ->
  // Design Users), not the module permission alone. The backend enforces the
  // same rule; these flags already account for super admin.
  // Also barred while filtered: handleDragEnd reuses "the order_index slots this
  // page occupies", and on a filtered page those are an arbitrary subset of the
  // global sequence, so one drag would scatter the queue for everyone. false
  // routes rendering to StaticRow, which has no drag context at all.
  const canReorder = canEdit && (metaData?.data?.hasReorderAccess ?? false) && !isFiltered;
  const canDeleteProject = canDelete && (metaData?.data?.hasDeleteAccess ?? false);

  // Plain position in the list on screen, continuing across pages (page 2 of
  // 20-per-page starts at 21). Used by the Completed and Cancelled tabs, and by
  // the Active tab while filtered.
  const visibleRows = activeTab === 'active' ? activeRows : (data?.data ?? []);
  const pageOffset = ((pagination?.page ?? page) - 1) * PAGE_SIZE;
  const rowNumberMap = new Map<string, number>();
  visibleRows.forEach((row, i) => rowNumberMap.set(row.id, pageOffset + i + 1));

  // The Active tab numbers each status from 1 — In Progress 1,2,3, On Hold
  // 1,2,3, Pending 1,2,3. The server's status_rank says where each status's count
  // stands on this page (the lowest rank it holds here), so page 2 continues
  // rather than restarting; local order supplies the rest, so a drag-reorder
  // renumbers immediately instead of waiting on a refetch.
  const statusAnchor = new Map<DmDesignProjectStatus, number>();
  visibleRows.forEach(row => {
    const lowest = statusAnchor.get(row.status);
    if (lowest === undefined || row.status_rank < lowest) statusAnchor.set(row.status, row.status_rank);
  });
  const statusNumberMap = new Map<string, number>();
  const seenPerStatus = new Map<DmDesignProjectStatus, number>();
  visibleRows.forEach(row => {
    const seen = seenPerStatus.get(row.status) ?? 0;
    statusNumberMap.set(row.id, statusAnchor.get(row.status)! + seen);
    seenPerStatus.set(row.status, seen + 1);
  });

  // Filtered lists keep plain row numbers: a per-status rank among matching rows
  // would read like queue position without being it. The Completed tab can't use
  // status_rank at all — it sorts by end_date, not the order_index the rank
  // follows — so both closed tabs stay positional.
  const rowNumber = (row: DmDesignProjectListItem) =>
    activeTab === 'active' && !isFiltered
      ? statusNumberMap.get(row.id)
      : rowNumberMap.get(row.id);

  // The Completed and Cancelled tabs render their empty message in two places —
  // the DataTable's `empty` prop and the mobile branch. One node for both, so
  // the filtered variant does not have to be written twice.
  const otherTabEmpty = (
    <div className="py-8 text-center">
      <p className="text-sm text-muted-foreground">
        {isFiltered
          ? EMPTY_FILTERED_TEXT
          : activeTab === 'cancelled' ? 'No cancelled projects' : 'No completed projects'}
      </p>
      {isFiltered && (
        <Button variant="outline" size="sm" className="mt-2" onClick={clearFilters}>
          <X className="w-3.5 h-3.5 mr-1" /> Clear filters
        </Button>
      )}
    </div>
  );

  const COLUMNS: Column<DmDesignProjectListItem>[] = [
    {
      key: 'no',
      header: 'No.',
      cell: row => {
        const n = rowNumber(row);
        return n != null
          ? <span className="text-sm font-medium text-muted-foreground">{n}</span>
          : <span className="text-muted-foreground text-sm">—</span>;
      },
      className: 'w-12 text-center',
    },
    {
      key: 'title',
      header: 'Title',
      cell: row => (
        <div className="min-w-[160px] max-w-[260px]">
          <p className="font-medium text-sm truncate">{row.title}</p>
          {row.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{row.description}</p>}
        </div>
      ),
    },
    {
      key: 'categories',
      header: 'Categories',
      cell: row => (
        <div className="flex flex-wrap gap-1 min-w-[120px] max-w-[200px]">
          {row.categories.length === 0 ? (
            // Categories are optional, so an empty cell is a real state, not a
            // rendering gap — mark it the way every other column marks empty.
            <span className="text-muted-foreground">—</span>
          ) : (
            <>
              {row.categories.slice(0, 2).map(c => {
                const catLabel = safeName(c.category?.name ?? c.custom_category_text);
                const sizeLabel = safeName(c.size?.name ?? c.custom_size_text);
                if (!catLabel) return null;
                return (
                  <Badge key={c.id} variant="secondary" className="text-xs whitespace-nowrap">
                    {catLabel}{sizeLabel ? ` · ${sizeLabel}` : ''}
                  </Badge>
                );
              })}
              {row.categories.length > 2 && <Badge variant="outline" className="text-xs">+{row.categories.length - 2}</Badge>}
            </>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: row => <StatusBadge project={row} canChangeStatus={canEdit && (metaData?.data?.hasStatusAccess ?? false) && row.status !== 'completed' && row.status !== 'cancelled'} />,
      className: 'w-28',
    },
    {
      key: 'publish_date',
      header: 'Publish Date',
      cell: row => row.publish_date
        ? <span className="text-sm whitespace-nowrap">{format(new Date(row.publish_date), 'd MMM yyyy')}</span>
        : <span className="text-muted-foreground">—</span>,
      className: 'w-28',
    },
    {
      key: 'start_date',
      header: 'Start Date',
      cell: row => row.start_date
        ? <span className="text-sm whitespace-nowrap">{format(new Date(row.start_date), 'd MMM yyyy')}</span>
        : <span className="text-muted-foreground">—</span>,
      className: 'w-28',
    },
    {
      key: 'end_date',
      header: 'End Date',
      cell: row => row.end_date
        ? <span className="text-sm whitespace-nowrap">{format(new Date(row.end_date), 'd MMM yyyy')}</span>
        : <span className="text-muted-foreground">—</span>,
      className: 'w-28',
    },
    {
      key: 'designer',
      header: 'Designer',
      cell: row => row.designer
        ? <span className="text-sm whitespace-nowrap">{row.designer.first_name} {row.designer.last_name}</span>
        : <span className="text-muted-foreground">—</span>,
      className: 'w-36',
    },
    {
      key: 'assigned_by',
      header: 'Assigned By',
      cell: row => row.assigned_by
        ? <span className="text-sm whitespace-nowrap">{row.assigned_by.first_name} {row.assigned_by.last_name}</span>
        : <span className="text-muted-foreground">—</span>,
      className: 'w-36',
    },
    {
      key: 'actions',
      header: '',
      cell: row => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" onClick={e => { e.stopPropagation(); setViewProjectId(row.id); }}>
            <Eye className="h-4 w-4" />
          </Button>
          {activeTab === 'active' && canEdit && (
            <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-teal-600 hover:text-teal-700" title="Edit"
              onClick={e => { e.stopPropagation(); setEditProject(row); }}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {activeTab === 'active' && canEdit && (
            <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" onClick={e => { e.stopPropagation(); setUploadProjectId(row.id); }}>
              <Image className="h-4 w-4" />
            </Button>
          )}
          {canDeleteProject && (
            <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" onClick={e => { e.stopPropagation(); setDeleteId(row.id); }}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Graphic Designer</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Design project tracking</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(metaData?.data?.hasDesignCategoryAccess) && (
            <Button variant="outline" onClick={() => setCategoriesOpen(true)}>
              <Settings className="w-4 h-4 mr-1" /> Design Categories
            </Button>
          )}
          {(canCreate || metaData?.data?.hasDesignCategoryAccess) && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Project
            </Button>
          )}
        </div>
      </div>

      {/* Counted from the list's own filters, so the cards follow the filter bar
          and the visibility pin exactly as the rows do. '—' until they load. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Total" value={counts?.total ?? '—'} icon={Layers} tone="teal" />
        <StatCard label={STATUS_LABELS.pending} value={counts?.pending ?? '—'} icon={Clock} tone="sky" />
        <StatCard label={STATUS_LABELS.in_progress} value={counts?.in_progress ?? '—'} icon={PlayCircle} tone="amber" />
        <StatCard label={STATUS_LABELS.in_hold} value={counts?.in_hold ?? '—'} icon={PauseCircle} tone="violet" />
        <StatCard label={STATUS_LABELS.completed} value={counts?.completed ?? '—'} icon={CheckCircle2} tone="emerald" />
        <StatCard label={STATUS_LABELS.cancelled} value={counts?.cancelled ?? '—'} icon={XCircle} tone="rose" />
      </div>

      <Tabs
        tabs={[
          { key: 'active', label: 'Active' },
          { key: 'completed', label: 'Completed' },
          { key: 'cancelled', label: 'Cancelled' },
        ]}
        active={activeTab}
        onChange={v => {
          const tab = v as 'active' | 'completed' | 'cancelled';
          // A filter whose dropdown the new tab hides would otherwise keep
          // narrowing the list with no way to see or clear it.
          const canPickDesigner = tab === 'completed' ? metaData?.data?.hasStatusAccess : metaData?.data?.hasCompletedAccess;
          if (!canPickDesigner) setDesignerFilter('');
          if (tab === 'completed' && !metaData?.data?.hasStatusAccess) setAssignedByFilter('');
          setActiveTab(tab);
          setPage(1);
        }}
      />

      {/* Heights: SearchInput ships a flat h-9 and DateRangePicker a flat h-10,
          while SelectTrigger is h-8 sm:h-10. Nudging the two flat ones onto that
          scale lines the row up at sm+, where it is horizontal and a mismatch
          shows; below sm everything stacks full-width and it cannot. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <SearchInput
          value={search}
          onChange={v => { setSearch(v); setPage(1); }}
          placeholder="Search by title or description…"
          containerClassName="w-full sm:w-64"
          className="sm:h-10"
        />

        <Select
          value={categoryFilter || 'all'}
          onValueChange={v => { setCategoryFilter(v === 'all' ? '' : v); setPage(1); }}
        >
          <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            <SelectItem value={DM_DESIGN_CUSTOM_CATEGORY}>Other</SelectItem>
          </SelectContent>
        </Select>

        {/* Hidden for users the list already pins to their own designs: for
            them picking anyone else ANDs to a permanently empty list. The
            Completed tab follows Status Update & Assigned By Access instead. */}
        {(activeTab === 'completed' ? metaData?.data?.hasStatusAccess : metaData?.data?.hasCompletedAccess) && (
          <Select
            value={designerFilter || 'all'}
            onValueChange={v => { setDesignerFilter(v === 'all' ? '' : v); setPage(1); }}
          >
            <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="All Designers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Designers</SelectItem>
              {(metaData?.data?.projectDesigners ?? []).map(d => (
                <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* On Active/Cancelled the list pins unprivileged users by designer_id,
            so this still narrows usefully ("what did X assign me?"). On
            Completed they are pinned to assigned_by_id = themselves, so it is
            hidden there unless they can see every completed project. */}
        {(activeTab !== 'completed' || metaData?.data?.hasStatusAccess) && (
          <Select
            value={assignedByFilter || 'all'}
            onValueChange={v => { setAssignedByFilter(v === 'all' ? '' : v); setPage(1); }}
          >
            <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="All Assigned By" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assigned By</SelectItem>
              {(metaData?.data?.projectAssigners ?? []).map(u => (
                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }}
          placeholder="Publish date range"
          className="h-8 sm:h-10 w-full sm:w-56"
        />

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}

        {pagination && (
          <span className="text-xs text-muted-foreground sm:ml-auto">
            {pagination.total} {pagination.total === 1 ? 'project' : 'projects'}
          </span>
        )}
      </div>

      {activeTab === 'active' ? (
        isLoading ? (
          <>
            <div className="hidden md:block rounded-md border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-3 py-2.5 w-8" />
                      {COLUMNS.map(col => (
                        <th key={col.key} className={`px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col.className ?? ''}`}>
                          {col.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="border-b animate-pulse">
                        <td className="pl-3 pr-1 py-2.5 w-8">
                          <div className="h-4 w-4 rounded bg-muted/60" />
                        </td>
                        {COLUMNS.map(col => (
                          <td key={col.key} className={`px-3 py-2.5 ${col.className ?? ''}`}>
                            <div className="h-4 rounded bg-muted/60" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="block md:hidden space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-lg border p-3 space-y-2 animate-pulse">
                  <div className="h-4 w-3/4 rounded bg-muted/60" />
                  <div className="h-3 w-1/2 rounded bg-muted/60" />
                </div>
              ))}
            </div>
          </>
        ) : isError ? (
          <div className="py-12 text-center">
            <p className="text-sm text-destructive">Failed to load projects</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>Retry</Button>
          </div>
        ) : activeRows.length === 0 ? (
          <div className="py-8 text-center">
            {isFiltered ? (
              <>
                <p className="text-sm text-muted-foreground">{EMPTY_FILTERED_TEXT}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={clearFilters}>
                  <X className="w-3.5 h-3.5 mr-1" /> Clear filters
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">No active projects</p>
                {(canCreate || metaData?.data?.hasDesignCategoryAccess) && (
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => setCreateOpen(true)}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Create first project
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:block rounded-md border overflow-hidden">
              <div className="overflow-x-auto">
                {canReorder ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={activeRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
                      <table className="w-full text-sm min-w-[1000px]">
                        <thead className="bg-muted/50 border-b">
                          <tr>
                            <th className="px-3 py-2.5 w-8" />
                            {COLUMNS.map(col => (
                              <th key={col.key} className={`px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col.className ?? ''}`}>
                                {col.header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {activeRows.map(row => (
                            <SortableRow key={row.id} row={row} columns={COLUMNS} onRowClick={() => setViewProjectId(row.id)} />
                          ))}
                        </tbody>
                      </table>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <table className="w-full text-sm min-w-[1000px]">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="px-3 py-2.5 w-8" />
                        {COLUMNS.map(col => (
                          <th key={col.key} className={`px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col.className ?? ''}`}>
                            {col.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeRows.map(row => (
                        <StaticRow key={row.id} row={row} columns={COLUMNS} onRowClick={() => setViewProjectId(row.id)} />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {pagination && (
                <PaginationBar page={pagination.page} totalPages={pagination.totalPages}
                  total={pagination.total} onPageChange={setPage} />
              )}
            </div>
            <div className="block md:hidden space-y-2">
              {activeRows.map(row => (
                <MobileProjectCard key={row.id} project={row} number={rowNumber(row)}
                  onClick={() => setViewProjectId(row.id)}
                  onEdit={canEdit ? () => setEditProject(row) : undefined} />
              ))}
              {pagination && (
                <PaginationBar page={pagination.page} totalPages={pagination.totalPages}
                  total={pagination.total} onPageChange={setPage} />
              )}
            </div>
          </>
        )
      ) : (
        <>
          <div className="hidden md:block">
            <DataTable
              columns={COLUMNS}
              rows={data?.data ?? []}
              rowKey={r => r.id}
              onRowClick={r => setViewProjectId(r.id)}
              loading={isLoading}
              error={isError}
              onRetry={refetch}
              empty={otherTabEmpty}
              pagination={pagination ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: setPage,
              } : undefined}
            />
          </div>
          <div className="block md:hidden space-y-2">
            {isLoading ? (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-lg border p-3 space-y-2 animate-pulse">
                    <div className="h-4 w-3/4 rounded bg-muted/60" />
                    <div className="h-3 w-1/2 rounded bg-muted/60" />
                  </div>
                ))}
              </>
            ) : (data?.data ?? []).length === 0 ? (
              otherTabEmpty
            ) : (
              <>
                {(data?.data ?? []).map(row => (
                  <MobileProjectCard key={row.id} project={row} number={rowNumber(row)} onClick={() => setViewProjectId(row.id)} />
                ))}
                {pagination && (
                  <PaginationBar page={pagination.page} totalPages={pagination.totalPages}
                    total={pagination.total} onPageChange={setPage} />
                )}
              </>
            )}
          </div>
        </>
      )}

      <CreateProjectDialog open={createOpen} onClose={() => setCreateOpen(false)} categories={categories}
        canSetAssignedBy={metaData?.data?.hasStatusAccess ?? false} />

      {viewProjectId && (
        <ViewProjectDialog projectId={viewProjectId} open={!!viewProjectId} onClose={() => setViewProjectId(null)} />
      )}

      {editProject && (
        <EditProjectDialog project={editProject} open={!!editProject}
          onClose={() => setEditProject(null)} categories={categories}
          canSetAssignedBy={metaData?.data?.hasStatusAccess ?? false} />
      )}

      <Dialog
        open={!!uploadProjectId}
        onOpenChange={v => { if (!v && !uploadMutation.isPending) { setUploadProjectId(null); setUploadFiles([]); } }}
      >
        {/* grid-cols override: see the Complete Design dialog. */}
        <DialogContent className="grid-cols-[minmax(0,1fr)]">
          <DialogHeader><DialogTitle>Upload Images</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wide">Select Images</Label>
                {uploadFiles.length > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    {validUploadFiles.length} ready
                    {invalidUploadCount > 0 && (
                      <span className="text-rose-600 dark:text-rose-400"> · {invalidUploadCount} invalid</span>
                    )}
                  </span>
                )}
              </div>

              <FilePicker
                multiple
                files={uploadFiles}
                onFilesChange={setUploadFiles}
                validate={validateDesignImage}
                accept={ALLOWED_IMAGE_ACCEPT}
                disabled={uploadMutation.isPending}
                placeholder={uploadFiles.length > 0 ? 'Add more images' : 'Choose images'}
                hint={`${ALLOWED_IMAGE_LABEL} · max ${MAX_PICK_LABEL} each`}
              />

              {invalidUploadCount > 0 ? (
                <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
                  Remove {invalidUploadCount} invalid file{invalidUploadCount > 1 ? 's' : ''} to continue.
                </p>
              ) : uploadFiles.length > 0 ? (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Sizes shown are the original files — each image is compressed before upload.
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={uploadMutation.isPending}
                onClick={() => { setUploadProjectId(null); setUploadFiles([]); }}
              >Cancel</Button>
              <Button
                disabled={validUploadFiles.length === 0 || invalidUploadCount > 0 || uploadMutation.isPending}
                onClick={() => uploadProjectId && uploadMutation.mutate({ id: uploadProjectId, files: validUploadFiles })}
              >{uploadMutation.isPending ? 'Uploading…' : 'Upload'}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the project and all its images.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && deleteMutation.mutate(deleteId)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DesignCategoriesModal open={categoriesOpen} onClose={() => setCategoriesOpen(false)} />
    </div>
  );
}
